
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Image, Star, Home, Pause, Play, XCircle, Info, RefreshCw } from 'lucide-react';
import { Ad } from '@/types/ads';

interface ActiveAdsOverviewProps {
  ads: Ad[];
  onViewDetails?: (adId: string) => void;
  onViewReceipt?: (adId: string) => void;
  onRenewClick?: (adId: string) => void;
}

const ActiveAdsOverview: React.FC<ActiveAdsOverviewProps> = ({ 
  ads, 
  onViewDetails, 
  onViewReceipt,
  onRenewClick 
}) => {
  // Helper function to determine icon based on ad type
  const getAdIcon = (type: string) => {
    switch (type) {
      case 'story':
        return <Star className="h-5 w-5" />;
      case 'beforeAfter':
        return <Image className="h-5 w-5" />;
      case 'homepage':
        return <Home className="h-5 w-5" />;
      case 'topStory':
        return <Star className="h-5 w-5" />;
      case 'featuredDoctor':
        return <Info className="h-5 w-5" />;
      case 'galleryPromotion':
        return <Image className="h-5 w-5" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  // Helper function to get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
      case 'paused':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Paused</Badge>;
      case 'expired':
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Expired</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  // Helper function to format ad type display
  const formatAdType = (type: string) => {
    switch (type) {
      case 'story': return 'Featured Story';
      case 'beforeAfter': return 'Featured Before/After';
      case 'homepage': return 'Homepage Spotlight';
      case 'topStory': return 'Top Story';
      case 'featuredDoctor': return 'Featured Doctor';
      case 'galleryPromotion': return 'Gallery Promotion';
      default: return type;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Active Promotions</CardTitle>
          <CardDescription>
            Your current promotional placements
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ads.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">You don't have any active promotions</p>
              <Button className="mt-4">Purchase Your First Promotion</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {ads.map((ad) => (
                <div 
                  key={ad.id} 
                  className={`p-4 rounded-lg border ${
                    ad.status === 'active' 
                      ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        ad.status === 'active' ? 'bg-amber-200 text-amber-700' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {getAdIcon(ad.type)}
                      </div>
                      <div>
                        <h3 className="font-medium">
                          {formatAdType(ad.type)}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {ad.status === 'active' ? (
                            <span className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" /> 
                              {ad.expiresIn} days remaining
                            </span>
                          ) : (
                            <span>Expired on {ad.expireDate}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(ad.status)}
                        {ad.isSponsoredByClinic && (
                          <Badge variant="outline" className="border-blue-300">
                            Clinic Sponsored
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {ad.status === 'active' ? (
                          <Button size="sm" variant="outline">
                            <Pause className="h-3 w-3 mr-1" /> Pause
                          </Button>
                        ) : ad.status === 'paused' ? (
                          <Button size="sm" variant="outline">
                            <Play className="h-3 w-3 mr-1" /> Resume
                          </Button>
                        ) : null}
                        
                        {ad.status === 'expired' && onRenewClick && (
                          <Button 
                            size="sm" 
                            className="bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={() => onRenewClick(ad.id)}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" /> Renew
                          </Button>
                        )}
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => onViewDetails && onViewDetails(ad.id)}
                        >
                          View Details
                        </Button>
                        
                        {onViewReceipt && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onViewReceipt(ad.id)}
                          >
                            Receipt
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        {ads.length > 0 && (
          <CardFooter className="flex justify-between">
            <p className="text-sm text-muted-foreground">
              {ads.filter(ad => ad.status === 'active').length} active promotions
            </p>
            <Button variant="outline" size="sm">
              Manage All Promotions
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default ActiveAdsOverview;
